import { FaceDetector, FilesetResolver, type FaceDetectorResult } from "@mediapipe/tasks-vision";
import { FACE_MODEL_PATH, WASM_PATH } from "../config/constants.ts";
import type { FaceDetectionBox } from "../utils/types.ts";

export class FaceTracker {
  private detector: FaceDetector | null = null;
  private lastTimestamp = -1;
  lastProcessMs = 0;
  lastError: string | null = null;

  async init(): Promise<void> {
    const vision = await FilesetResolver.forVisionTasks(WASM_PATH);
    const options = {
      runningMode: "VIDEO" as const,
      minDetectionConfidence: 0.5,
    };
    try {
      this.detector = await FaceDetector.createFromOptions(vision, {
        ...options,
        baseOptions: { modelAssetPath: FACE_MODEL_PATH, delegate: "GPU" },
      });
    } catch {
      this.detector = await FaceDetector.createFromOptions(vision, {
        ...options,
        baseOptions: { modelAssetPath: FACE_MODEL_PATH, delegate: "CPU" },
      });
    }
  }

  detect(video: HTMLVideoElement, timestamp: number): FaceDetectionBox[] {
    if (!this.detector) return [];
    if (timestamp <= this.lastTimestamp) timestamp = this.lastTimestamp + 1;
    const started = performance.now();
    try {
      const result: FaceDetectorResult = this.detector.detectForVideo(video, timestamp);
      this.lastTimestamp = timestamp;
      this.lastError = null;
      this.lastProcessMs = performance.now() - started;
      const width = video.videoWidth || 1;
      const height = video.videoHeight || 1;
      return (result.detections ?? []).map((detection) => {
        const box = detection.boundingBox;
        const score = detection.categories?.[0]?.score ?? 0;
        return {
          score,
          box: {
            x: box?.originX ?? 0,
            y: box?.originY ?? 0,
            width: box?.width ?? width,
            height: box?.height ?? height * 0.45,
          },
        };
      });
    } catch (error) {
      this.lastError = error instanceof Error ? error.message : "Face detector failed.";
      this.lastProcessMs = performance.now() - started;
      return [];
    }
  }

  close(): void {
    this.detector?.close();
    this.detector = null;
    this.lastTimestamp = -1;
  }
}
